import { z } from 'zod';
import { BOOST_BARS, CALLINGS, COOLDOWN_GROUPS, COUNTERS, DISCIPLINES, GEAR_SLOTS, QUALITIES, SKILLS, STATS, TOWNS } from './ids.ts';

export const statSchema = z.enum(STATS);
export const skillIdSchema = z.enum(SKILLS);
export const callingIdSchema = z.enum(CALLINGS);
export const townIdSchema = z.enum(TOWNS);
export const disciplineSchema = z.enum(DISCIPLINES);
export const qualitySchema = z.enum(QUALITIES);
/** A game-icons.net icon name (the web app draws it); catalog.test.ts checks it exists. */
export const iconSchema = z.string().regex(/^[a-z0-9]+(-[a-z0-9]+)*$/);

export const itemSchema = z.object({
  key: z.string().regex(/^[a-z][a-z0-9-]*$/),
  name: z.string().min(1),
  icon: iconSchema,
  kind: z.enum(['weapon', 'armour', 'mount', 'tool', 'ammo', 'consumable', 'material']),
  slot: z.enum(GEAR_SLOTS).optional(),
  /** What it does in a fight at common quality; anything with a slot has one. Quality scales it. */
  combat: z
    .object({
      /** Base damage of a hit, before Strength, Defence and armour. */
      damage: z.number().positive().optional(),
      /** Fraction of incoming damage it turns aside. */
      armour: z.number().gt(0).lt(1).optional(),
      /** Added to hit chance. */
      accuracy: z.number().min(-0.5).max(0.5).optional(),
      /** The kind of ammunition it looses, one per shot, from the pack (ranged weapons): the best held goes first. */
      ammo: z.string().optional(),
      /** Added to the rider's Speed, as a fraction (mounts). */
      speed: z.number().gt(0).lt(1).optional(),
    })
    .optional(),
  /** Tools only: held in the pack, the best for a skill adds `bonus` (scaled by quality) to its XP and to graded goods' quality score. */
  tool: z.object({ skill: skillIdSchema, bonus: z.number().gt(0).lt(1) }).optional(),
  /** Ammunition only: the kind it is (what a bow's `combat.ammo` names). Its `combat.damage` is added to each hit. */
  ammo: z.string().optional(),
  description: z.string(),
  /** Base worth in coin at common quality: what a trader at the Market Cross reckons it at. Unset means nobody will haggle for it. */
  value: z.number().int().positive().optional(),
  /** What drinking or eating it does. Only consumables have one. */
  use: z
    .object({
      /** Life restored, as a fraction of max Life. */
      lifeFraction: z.number().min(0).max(1).optional(),
      /** Minutes taken off an Infirmary stay. */
      infirmaryMinutes: z.number().int().positive().optional(),
      /** Flat amounts added to bars, at common quality. They may lift a bar above its max (Mood above its base). */
      bars: z.partialRecord(z.enum(BOOST_BARS), z.number().int().positive()).optional(),
      /** Nothing else in the group may be taken until this runs out. */
      cooldown: z.object({ group: z.enum(COOLDOWN_GROUPS), minutes: z.number().int().positive() }).optional(),
    })
    .optional(),
});

export const skillSchema = z.object({
  id: skillIdSchema,
  name: z.string(),
  discipline: disciplineSchema,
  icon: iconSchema,
});

export const callingSchema = z.object({
  id: callingIdSchema,
  name: z.string(),
  icon: iconSchema,
  affinity: z.array(skillIdSchema).length(3),
  perk: z.object({ name: z.string(), description: z.string() }),
  kit: z.array(z.object({ item: z.string(), quantity: z.number().int().positive() })).min(1),
  bonusCoin: z.number().int().nonnegative(),
  /** How the Calling fares in a fight at the start, in a sentence, shown when choosing one. */
  fighting: z.object({ rating: z.enum(['strong', 'fair', 'weak']), note: z.string() }),
});

export const venueSchema = z.object({
  key: z.string().regex(/^[a-z][a-z0-9-]*$/),
  name: z.string(),
  town: townIdSchema,
  skill: skillIdSchema,
  icon: iconSchema,
  description: z.string(),
});

const key = z.string().regex(/^[a-z][a-z0-9-]*$/);
const bar = z.enum(['vigour', 'nerve', 'focus']);

/** Fields every minigame activity shares: a level gate, a bar cost, base XP and optional inputs or stake. */
const activityBase = {
  key,
  name: z.string(),
  description: z.string().optional(),
  /** A heading the venue lists it under, when it has many ("Iron arms", "Plate"). */
  group: z.string().optional(),
  /** Skill level needed to try it. */
  level: z.number().int().min(1).max(100),
  cost: z.object({ bar, amount: z.number().int().positive() }),
  /** Items consumed when the attempt starts. Optional rather than defaulted: content is not parsed at runtime. */
  inputs: z.array(z.object({ item: z.string(), quantity: z.number().int().positive() })).optional(),
};

const miningActivity = z.object({
  ...activityBase,
  /** Ore is smelted on the way out; this is the ingot it makes. */
  ingot: z.string(),
  ore: z.number().int().positive(),
  unstable: z.number().int().positive(),
});
const woodcuttingActivity = z.object({
  ...activityBase,
  log: z.string(),
  logs: z.tuple([z.number().int().nonnegative(), z.number().int().positive()]),
  /** Clean cuts needed to fell it. */
  hp: z.number().positive(),
  /** How fast the axe winds back. */
  rate: z.number().positive(),
  /** Width of the gold band, in percent of the arc. */
  band: z.number().positive(),
  trunkWidth: z.number().positive(),
  crown: z.number().positive(),
});
const fishingActivity = z.object({
  ...activityBase,
  catch: z.string(),
  /** How hard it pulls. */
  fight: z.number().positive(),
});
const diceActivity = z.object({
  ...activityBase,
  stake: z.number().int().positive(),
  /** The house banks once its pot reaches this. */
  houseBanks: z.number().int().positive(),
  /** A cannier house that plays the score. */
  smart: z.boolean().optional(),
});

/** The graded good an activity makes. */
const product = z.string();
const range = z.tuple([z.number().nonnegative(), z.number().positive()]);

const smithingActivity = z.object({
  ...activityBase,
  product,
  metal: z.enum(['bronze', 'iron', 'steel', 'damascus', 'osmund', 'starIron']),
  /** Hammer blows in the shaping phase. */
  strikes: z.number().int().positive(),
});
const brewingActivity = z.object({
  ...activityBase,
  product,
  /** Temperature rests in the mash schedule. */
  steps: z.number().int().positive(),
  /** Half-width of the target band, °C. */
  band: z.number().positive(),
  /** No gold band on the fermentation gauge, and each batch peaks at its own height: rack on the turn. */
  unmarked: z.boolean().optional(),
});
const horsemanshipActivity = z.object({
  ...activityBase,
  product,
  /** Seconds in the saddle to calm it. */
  duration: z.number().positive(),
  wild: z.number().positive(),
});
const lockpickingActivity = z.object({
  ...activityBase,
  pins: z.number().int().positive(),
  loot: range,
  /** Seconds before the watchman's lantern burns out. */
  time: z.number().positive(),
  /** The chance each pin is a security pin, with a false set that feels nearly right. */
  security: z.number().min(0).max(1).optional(),
});
const archeryActivity = z.object({
  ...activityBase,
  prize: z.number().int().positive(),
  /** Entry fee, paid on the way in. */
  stake: z.number().int().positive(),
  arrows: z.number().int().positive(),
  wind: z.number().nonnegative(),
  /** Range multiplier: a smaller target and more drop. */
  distance: z.number().positive(),
});
const illuminationActivity = z.object({
  ...activityBase,
  product,
  strokes: z.number().int().positive(),
  /** How far from the guide the pen may stray, in canvas units. */
  tolerance: z.number().positive(),
});
const joustingActivity = z.object({
  ...activityBase,
  prize: z.number().int().positive(),
  /** Entry fee, paid on the way in. */
  stake: z.number().int().positive(),
  /** How good the opponent is, 0–1. */
  opponent: z.number().min(0).max(1),
  foe: z.string(),
});
const huntingActivity = z.object({
  ...activityBase,
  product,
  /** The chase is size × size. */
  size: z.number().int().min(3).max(10),
  turns: z.number().int().positive(),
  /** How fast the strike ring closes. */
  speed: z.number().positive(),
  /** Small game is taken with the falcon, large with the bow. */
  bird: z.boolean().optional(),
});
const siegeActivity = z.object({
  ...activityBase,
  prize: z.number().int().positive(),
  hp: z.number().int().positive(),
  wallHeight: z.number().positive(),
  distance: range,
  wind: z.number().nonnegative(),
  shots: z.number().int().positive(),
});
const cardsActivity = z.object({
  ...activityBase,
  stake: z.number().int().positive(),
  /** Suspicion each peek at the marks raises. */
  heat: z.number().positive(),
});
const alchemyActivity = z.object({
  ...activityBase,
  product,
  /** Reagents in the hidden recipe. */
  reagents: z.number().int().positive(),
  volatility: z.number().int().positive(),
});
const hagglingActivity = z.object({
  ...activityBase,
  /** The trader's secret limit, as a multiple of the ware's value. */
  reserve: range,
  patience: z.number().int().positive(),
  /** How often a tell lies. */
  noise: z.number().min(0).max(1),
  trader: z.string(),
  /** The most the trader will pay for anything. */
  purse: z.number().int().positive(),
});

const game = <E extends string, A extends z.ZodType>(engine: E, activity: A) =>
  z.object({ venue: key, engine: z.literal(engine), activities: z.array(activity).min(1) });

/** A venue's minigame: which engine runs it and the activities on offer. */
export const minigameSchema = z.discriminatedUnion('engine', [
  game('mining', miningActivity),
  game('woodcutting', woodcuttingActivity),
  game('fishing', fishingActivity),
  game('dice', diceActivity),
  game('smithing', smithingActivity),
  game('brewing', brewingActivity),
  game('horsemanship', horsemanshipActivity),
  game('lockpicking', lockpickingActivity),
  game('archery', archeryActivity),
  game('illumination', illuminationActivity),
  game('jousting', joustingActivity),
  game('hunting', huntingActivity),
  game('siege', siegeActivity),
  game('cards', cardsActivity),
  game('alchemy', alchemyActivity),
  game('haggling', hagglingActivity),
]);

export const townSchema = z.object({
  id: townIdSchema,
  name: z.string(),
  icon: iconSchema,
  description: z.string(),
  yardStat: statSchema,
  capital: z.boolean(),
});

export const routeSchema = z.object({
  from: townIdSchema,
  to: townIdSchema,
  minutes: z.number().int().positive(),
});

export const crimeSchema = z.object({
  key: z.string().regex(/^[a-z][a-z0-9-]*$/),
  name: z.string(),
  icon: iconSchema,
  description: z.string(),
  nerve: z.number().int().positive(),
  /** Chance of success at level 1, before level bonuses. */
  successChance: z.number().gt(0).lt(1),
  /** On failure: chance the watch catches you (Gaol). Otherwise you slip away, or suffer a mishap. */
  catchChance: z.number().min(0).max(1),
  coin: z.object({ min: z.number().int().nonnegative(), max: z.number().int().nonnegative() }),
  gaolMinutes: z.number().int().nonnegative(),
  /** Failing without being caught can still go badly: a fall, a bite, a beating. */
  mishap: z.object({ chance: z.number().min(0).max(1), life: z.number().int().positive(), infirmaryMinutes: z.number().int().positive(), text: z.string() }).optional(),
  /** Poaching counts for the Ranger's Woodcraft perk. */
  poaching: z.boolean().default(false),
  /** Towns where it can be done; omitted means everywhere. */
  towns: z.array(townIdSchema).optional(),
  success: z.string(),
  failure: z.string(),
  caught: z.string(),
});

export const shopSchema = z.object({
  key: z.string().regex(/^[a-z][a-z0-9-]*$/),
  name: z.string(),
  icon: iconSchema,
  description: z.string(),
  towns: z.array(townIdSchema).min(1),
  stock: z.array(z.object({ item: z.string(), price: z.number().int().positive() })).min(1),
});

/** What a mastery tier's passive does. game-core reads these; the description is for players. */
export const passiveEffectSchema = z.discriminatedUnion('kind', [
  /** A bar's max (Mood: its base) is raised. */
  z.object({ kind: z.literal('barMax'), bar: z.enum(['vigour', 'focus', 'nerve', 'mood']), amount: z.number().int().positive() }),
  /** More XP in the Discipline's skills. */
  z.object({ kind: z.literal('xp'), bonus: z.number().gt(0).lt(1) }),
  /** Combat stats in a fight. */
  z.object({ kind: z.literal('fightStats'), stats: z.array(statSchema).min(1), bonus: z.number().gt(0).lt(1) }),
  /** Vigour an attack costs is lowered. */
  z.object({ kind: z.literal('attackCost'), amount: z.number().int().positive() }),
  /** Added to the quality score of graded goods made in the Discipline's venues. */
  z.object({ kind: z.literal('quality'), bonus: z.number().gt(0).lt(1) }),
  /** Chance a craft consumes no materials. */
  z.object({ kind: z.literal('thrift'), chance: z.number().gt(0).lt(1) }),
  /** Taken off the market fee (a fraction of proceeds). */
  z.object({ kind: z.literal('marketFee'), reduction: z.number().gt(0).lt(1) }),
  /** Multiplies time in gaol, or in the Infirmary. */
  z.object({ kind: z.literal('gaolTime'), mult: z.number().gt(0).lt(1) }),
  z.object({ kind: z.literal('infirmaryTime'), mult: z.number().gt(0).lt(1) }),
  /** Consumables do this much more. */
  z.object({ kind: z.literal('consumables'), bonus: z.number().gt(0).lt(1) }),
]);

/** A Discipline title, earned at a total of `levels` across its four skills, with one passive. */
export const masterySchema = z.object({
  discipline: disciplineSchema,
  tier: z.number().int().min(1).max(4),
  levels: z.number().int().positive(),
  title: z.string(),
  passive: z.object({ description: z.string(), effect: passiveEffectSchema }),
});

/**
 * A cross-skill recipe, made at a venue without a minigame: it needs a level in each listed skill,
 * takes its inputs and a bar cost, and gives XP to every listed skill.
 */
export const recipeSchema = z.object({
  key,
  name: z.string(),
  description: z.string(),
  /** A heading the venue lists it under, when it has many. */
  group: z.string().optional(),
  venue: key,
  skills: z.array(z.object({ skill: skillIdSchema, level: z.number().int().min(1).max(100) })).min(2),
  cost: z.object({ bar, amount: z.number().int().positive() }),
  inputs: z.array(z.object({ item: z.string(), quantity: z.number().int().positive() })).min(1),
  product: z.object({ item: z.string(), quantity: z.number().int().positive() }),
});

/**
 * What a deed asks, read off the player's record (game-core `deedProgress`): a lifetime count, a feat
 * done so many times (`engine.feat`), games finished at a venue (or one of its activities), or how many
 * venues or towns have been tried.
 */
export const deedTestSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('count'), counter: z.enum(COUNTERS), at: z.number().int().positive() }),
  z.object({ kind: z.literal('feat'), feat: z.string().regex(/^[a-z]+\.[a-z0-9-]+$/), at: z.number().int().positive() }),
  z.object({ kind: z.literal('plays'), venue: key, activity: key.optional(), at: z.number().int().positive() }),
  z.object({ kind: z.literal('venues'), at: z.number().int().positive() }),
  z.object({ kind: z.literal('towns'), at: z.number().int().positive() }),
]);

/**
 * A deed: done once, for good. Its reward is Renown, a title the player may go by, a few common goods,
 * or nothing but the telling. Hidden deeds show as "???" until done.
 */
export const deedSchema = z.object({
  key,
  name: z.string().min(1),
  /** One line, in the realm's voice. */
  flavour: z.string().min(1),
  /** What to do, said plainly; shown for visible deeds before they are done. */
  hint: z.string().min(1),
  test: deedTestSchema,
  reward: z.object({
    renown: z.number().int().positive().optional(),
    title: z.string().min(1).optional(),
    item: z.object({ item: key, quantity: z.number().int().positive().max(5) }).optional(),
  }),
  hidden: z.boolean(),
});
